import React, { useEffect, useRef } from "react";
import { Text, TouchableOpacity, View, Modal } from "react-native";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { TEXT_STYLES } from "./BarcodeScannerStyles";

/**
 * Expo CameraView on web often shows preview but does not fire onBarcodeScanned for 1D symbologies on iOS Safari.
 * ZXing reads frames from a plain HTML video stream and decodes reliably on mobile web.
 */
const BarcodeScannerWeb = ({ closeScanner, onBarCodeScanned, unmounCamera }) => {
  const videoRef = useRef(null);
  const locked = useRef(false);

  useEffect(() => {
    locked.current = false;
    const reader = new BrowserMultiFormatReader();
    let cancelled = false;
    let scannerControls = null;

    const handler = (result, _error, controls) => {
      if (cancelled || locked.current) return;
      if (!result) return;
      const text = result.getText();
      if (!text) return;
      locked.current = true;
      try {
        controls?.stop();
      } catch (_) {
        /* ignore */
      }
      try {
        scannerControls?.stop();
      } catch (_) {
        /* ignore */
      }
      onBarCodeScanned(text);
      unmounCamera();
    };

    const start = async () => {
      await new Promise((r) => setTimeout(r, 200));
      if (cancelled) return;
      const video = videoRef.current;
      if (!video) return;
      try {
        scannerControls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: "environment" } }, audio: false },
          video,
          handler
        );
      } catch (e1) {
        if (cancelled) return;
        try {
          scannerControls = await reader.decodeFromVideoDevice(undefined, video, handler);
        } catch (e2) {
          console.warn("BarcodeScannerWeb:", e1, e2);
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      locked.current = true;
      try {
        scannerControls?.stop();
      } catch (_) {
        /* ignore */
      }
    };
  }, [onBarCodeScanned, unmounCamera]);

  const onCancel = () => {
    locked.current = true;
    closeScanner();
    unmounCamera();
  };

  const videoEl =
    typeof document !== "undefined"
      ? React.createElement("video", {
          ref: videoRef,
          style: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
          autoPlay: true,
          muted: true,
          playsInline: true,
        })
      : null;

  return (
    <Modal transparent visible animationType="slide">
      <View style={{ flex: 1, position: "relative", backgroundColor: "#000" }}>
        <View style={TEXT_STYLES.cameraLeftOptionsContainer}>
          <TouchableOpacity onPress={onCancel} style={TEXT_STYLES.switchCameraButton}>
            <Text style={TEXT_STYLES.middle}>CANCEL</Text>
          </TouchableOpacity>
        </View>
        <View style={TEXT_STYLES.camera}>{videoEl}</View>
      </View>
    </Modal>
  );
};

export default BarcodeScannerWeb;
